import {
  faCircleInfo,
  faCopy,
  faExclamationTriangle,
  faLink,
  faPlus,
  faSync,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Divider,
  Group,
  List,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { isNull } from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { apiAddress } from "../../config/server";
import CalendarApi from "../../data/api/calendarApi";
import { ICalendarLink } from "../../types/calendar/Calendar";
import { CenteredLoader } from "../common/loader";

interface CalendarManagerProps {
  // Empty interface for future extensibility
}

const CalendarManager: React.FunctionComponent<CalendarManagerProps> = () => {
  const [links, setLinks] = React.useState<ICalendarLink[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isGenerating, setIsGenerating] = React.useState<boolean>(false);
  const [tokenToRevoke, setTokenToRevoke] = React.useState<ICalendarLink | null>(null);
  const [isRevoking, setIsRevoking] = React.useState<boolean>(false);

  const base = `${apiAddress}/api/calendar/sync/`;

  const reloadCalendar = React.useCallback(() => {
    CalendarApi.getMyLinks()
      .then((fetchedLinks) => setLinks(fetchedLinks))
      .catch((error: unknown) => setError(error instanceof Error ? error.message : "Failed to load calendar links"));
  }, []);

  React.useEffect(() => {
    reloadCalendar();
  }, [reloadCalendar]);

  const generateLink = () => {
    setIsGenerating(true);
    setError(null);
    CalendarApi.generateLink()
      .then(() => reloadCalendar())
      .catch((error: unknown) => setError(error instanceof Error ? error.message : "Could not generate link"))
      .finally(() => setIsGenerating(false));
  };

  const revokeLink = (tokenId: string) => {
    setIsRevoking(true);
    setError(null);
    CalendarApi.deleteLink(tokenId)
      .then(() => {
        setTokenToRevoke(null);
        reloadCalendar();
      })
      .catch((error: unknown) => setError(error instanceof Error ? error.message : "Could not revoke link"))
      .finally(() => setIsRevoking(false));
  };

  const formatDate = (d?: Date | DateTime | null) => {
    if (!d) return "—";

    let dateToFormat: DateTime;
    if (d instanceof Date) {
      dateToFormat = DateTime.fromJSDate(d);
    } else if (DateTime.isDateTime(d)) {
      dateToFormat = d;
    } else if (typeof d === 'object' && 'toJSDate' in d) {
      // Handle Luxon DateTime-like objects
      dateToFormat = DateTime.fromJSDate((d as any).toJSDate());
    } else {
      return "—";
    }

    if (!dateToFormat.isValid) return "—";
    return dateToFormat.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  };

  const hasLinks = (links?.length || 0) > 0;

  return (
    <Stack gap="md">
      <Card withBorder shadow="sm" padding="lg" radius="md">
        <Group align="flex-start" justify="space-between">
          <Group align="flex-start" gap="sm">
            <FontAwesomeIcon icon={faLink} style={{ fontSize: 24, color: 'var(--color-button-primary)' }} />
            <div>
              <h4 style={{ margin: 0 }}>Calendar Links</h4>
              <Text size="sm" c="dimmed" style={{ lineHeight: 1.4, marginTop: 4 }}>
                Subscribe to your personal schedule from iOS, Google Calendar, Outlook and other calendar apps.
                The feed is read-only and may take up to 15 minutes to refresh in external clients.
              </Text>
            </div>
          </Group>
          <div>
            {!hasLinks && !isNull(links) && (
              <Button leftSection={<FontAwesomeIcon icon={faPlus} />} loading={isGenerating} onClick={generateLink}>
                Generate Link
              </Button>
            )}
          </div>
        </Group>

        {error && (
          <Alert color="red" mt="md" variant="light" radius="md">
            <Group gap="xs">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </Group>
          </Alert>
        )}

        {isNull(links) && !error && (
          <div style={{ padding: '24px 0 8px 0' }}>
            <CenteredLoader />
          </div>
        )}

        {hasLinks && (
          <Stack gap="sm" mt="md">
            {links!.map((l) => {
              const url = base + l._id;
              return (
                <Card key={l._id} withBorder padding="md" radius="md" style={{ background: 'var(--color-admin-elevated)' }}>
                  <Stack gap={6}>
                    <Group justify="space-between" align="flex-start">
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Group gap="xs">
                          <Badge color="blue" variant="light" size="sm">Active</Badge>
                          {l.dateLastAccessed && <Badge color="green" variant="light" size="sm">Accessed</Badge>}
                        </Group>
                        <Code block style={{ fontSize: 12, position: 'relative', paddingRight: 44 }}>
                          {url}
                        </Code>
                        <Text size="xs" c="dimmed">
                          Created: {formatDate(l.dateCreated)} | Last Accessed: {formatDate(l.dateLastAccessed)}
                        </Text>
                      </Stack>
                      <Stack gap={4} align="flex-end" style={{ minWidth: 48 }}>
                        <CopyButton value={url} timeout={1500}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied!' : 'Copy URL'} withArrow position="left">
                              <ActionIcon variant="light" color={copied ? 'teal' : 'blue'} onClick={copy} aria-label="Copy calendar URL">
                                <FontAwesomeIcon icon={faCopy} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                        <Tooltip label="Open link" withArrow position="left">
                          <ActionIcon component="a" href={url} target="_blank" rel="noopener noreferrer" variant="light" color="gray" aria-label="Open calendar feed">
                            <FontAwesomeIcon icon={faLink} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Revoke" withArrow position="left">
                          <ActionIcon variant="light" color="red" onClick={() => setTokenToRevoke(l)} aria-label="Revoke calendar link">
                            <FontAwesomeIcon icon={faTrash} />
                          </ActionIcon>
                        </Tooltip>
                      </Stack>
                    </Group>
                  </Stack>
                </Card>
              );
            })}
            <Group justify="flex-start" mt="xs" gap="xs">
              <Button variant="outline" size="sm" leftSection={<FontAwesomeIcon icon={faSync} />} loading={isGenerating} onClick={generateLink}>
                Regenerate
              </Button>
              <Text size="xs" c="dimmed">
                Regenerating will create a new link and keep existing ones active until you revoke them.
              </Text>
            </Group>
          </Stack>
        )}
      </Card>

      <Card withBorder padding="lg" radius="md" shadow="sm">
        <Group align="flex-start" gap="sm" mb="xs">
          <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: 18, color: 'var(--color-button-primary)' }} />
          <h5 style={{ margin: 0 }}>How to Add to Your Calendar</h5>
        </Group>
        <Text size="sm" c="dimmed" mb="sm" style={{ lineHeight: 1.5 }}>
          Use the generated URL as a subscription (not an import) so changes automatically show up.
        </Text>
        <Divider my="sm" />
        <List spacing="xs" withPadding>
          <List.Item>
            <Anchor
              href="https://support.apple.com/en-gb/guide/iphone/iph3d1110d4/ios#set-up-a-calendar:~:text=Subscribe%20to%20an%20external%2C%20read%2Donly%20calendar%3A"
              target="_blank"
              rel="noreferrer"
            >
              iPhone (iOS) instructions
            </Anchor>
          </List.Item>
          <List.Item>
            <Anchor
              href="https://support.google.com/calendar/answer/37100?hl=en&co=GENIE.Platform%3DDesktop#:~:text=Use%20a%20link%20to%20add%20a%20public%20calendar"
              target="_blank"
              rel="noreferrer"
            >
              Google Calendar instructions
            </Anchor>
          </List.Item>
          <List.Item>
            <Anchor
              href="https://support.microsoft.com/en-us/office/import-or-subscribe-to-a-calendar-in-outlook-on-the-web-503ffaf6-7b86-44fe-8dd6-8099d95f38df#:~:text=your%20organization%27s%20directory-,Instructions%20for%20classic%20Outlook%20on%20the%20web,-Import%20a%20calendar"
              target="_blank"
              rel="noreferrer"
            >
              Outlook instructions
            </Anchor>
          </List.Item>
        </List>
      </Card>

      <Modal
        opened={!!tokenToRevoke}
        onClose={() => setTokenToRevoke(null)}
        title={<Group gap="sm"><FontAwesomeIcon icon={faTrash} /> <span>Revoke Calendar Link</span></Group>}
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to revoke this calendar link? Existing subscriptions will stop updating after revocation.
          </Text>
          <Code block>{tokenToRevoke ? base + tokenToRevoke._id : ''}</Code>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setTokenToRevoke(null)}>Cancel</Button>
            <Button color="red" loading={isRevoking} leftSection={<FontAwesomeIcon icon={faTrash} />} onClick={() => tokenToRevoke && revokeLink(tokenToRevoke._id)}>Revoke</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default CalendarManager;
